import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, InputNumber, Slider, Select, Input, Progress, Switch, Modal, message } from 'antd';
import {
  Film,
  Music,
  Type,
  ArrowUp,
  ArrowDown,
  Trash2,
  Plus,
  ListMusic,
  Play,
  Pause,
  Scissors,
  Lock,
  Sparkles,
  Mic,
} from 'lucide-react';
import { VideoTrimmer } from '../../components/studio/video-trimmer';
import {
  FilterPicker,
  MotionPicker,
  TransitionPicker,
  TrendPicker,
  useSampleFrames,
} from '../../components/studio/effect-preview';
import { Timeline } from '../../components/studio/timeline';
import { getCabPlusStatus } from '../../api/cab-plus/cab-plus.api';
import { RootState } from '../../configuration';
import {
  startCompose,
  getJobStatus,
  ComposeResult,
  getMusicLibrary,
  getMusicPreviewUrl,
  getHighlights,
  MusicTrack,
  MusicCategory,
  synthesizeVoice,
  VOICE_OPTIONS,
  getVoiceQuota,
  buyVoicePack,
  VoiceQuota,
} from '../../api/media/video-editor.api';
import { createNewPost } from '../../api/posts/fetch-post.api';
import { getAffRef } from '../../utils/common/aff-ref';

interface Clip {
  file: File;
  url: string;
  duration: number;
  start: number;
  end: number;
}
interface TextItem {
  text: string;
  pos: 'top' | 'center' | 'bottom';
  size: number;
  color: string;
  start: number;
  end: number;
  anim?: string; // ③ chữ động: '' | fade | slidein | slideup
}

const MAX_CLIPS = 5;
const MAX_TOTAL = 60;
const MAX_MB = 60; // trần dung lượng mỗi clip (hạng Creator); thường 15MB / donate 35MB — server kiểm chính xác
const POS_Y: Record<string, number> = { top: 0.1, center: 0.5, bottom: 0.9 };

// Hiệu ứng động (cháy vào video, render server-side ffmpeg). start/end tính bằng giây trên video kết quả.
type EffectType = 'scoreboard' | 'varCard' | 'blinkingCta' | 'flashSale';
interface EffectItem { type: EffectType; text: string; color: string; start: number; end: number }
const EFFECT_META: Record<EffectType, { label: string; emoji: string; defText: string; defColor: string }> = {
  scoreboard: { label: 'Bảng tỉ số chạy ngang', emoji: '🏟️', defText: 'CAB FC  2 - 1  ĐỐI THỦ', defColor: 'white' },
  varCard: { label: 'Thẻ VAR', emoji: '🟥', defText: 'VAR CHECK', defColor: 'white' },
  blinkingCta: { label: 'Nút kêu gọi nhấp nháy', emoji: '✨', defText: 'MUA NGAY', defColor: '#FACC15' },
  flashSale: { label: 'Chớp Flash Sale', emoji: '⚡', defText: 'FLASH SALE', defColor: 'red' },
};

// Template Hub: mỗi mẫu prefill bố cục chữ + HIỆU ỨNG ĐỘNG + gợi ý (giữ/ tắt tiếng gốc). Người dùng sửa nội dung + chọn nhạc.
type PresetText = { text: string; pos: 'top' | 'center' | 'bottom'; size: number; color: string; anim?: string };
type PresetEffect = { type: EffectType; text?: string; color?: string; start?: number; end?: number };
type StudioPreset = { id: string; name: string; emoji: string; mute: boolean; hint: string; texts: PresetText[]; effects?: PresetEffect[]; transition?: string; colorFilter?: string; motion?: string; trend?: string };
const PRESETS: StudioPreset[] = [
  { id: 'vlog', name: 'Vlog / Đời thường', emoji: '🎙️', mute: false, hint: 'Giữ giọng nói gốc + nhạc nền nhẹ', transition: 'fade', colorFilter: 'warm', motion: 'zoomin',
    texts: [{ text: 'Chú thích của bạn…', pos: 'bottom', size: 38, color: 'white', anim: 'fade' }] },
  { id: 'sport', name: 'Bóng đá / Thể thao', emoji: '⚽', mute: true, hint: 'Nhạc sôi động; bảng tỉ số chạy + thẻ VAR', transition: 'slideleft', colorFilter: 'vivid', trend: 'shake',
    texts: [{ text: 'Bình luận…', pos: 'bottom', size: 36, color: 'white', anim: 'slidein' }],
    effects: [{ type: 'scoreboard', text: 'CAB FC  2 - 1  ĐỐI THỦ' }, { type: 'varCard', text: 'VAR CHECK', start: 2, end: 4 }] },
  { id: 'review', name: 'Review / Bán hàng', emoji: '🛍️', mute: true, hint: 'Giá + nút mua nhấp nháy + chớp flash-sale', transition: 'zoomin', colorFilter: 'vivid', motion: 'zoomin',
    texts: [{ text: 'Giá: 199.000đ', pos: 'top', size: 44, color: '#FACC15', anim: 'slideup' }],
    effects: [{ type: 'flashSale', text: 'FLASH SALE', start: 0.5, end: 3 }, { type: 'blinkingCta', text: 'MUA NGAY 👉' }] },
  { id: 'meme', name: 'Meme', emoji: '😂', mute: true, hint: 'Chữ to ở trên; thẻ VAR "phán" cho vui', transition: 'fade', colorFilter: '', trend: 'glitch',
    texts: [{ text: 'Khi bạn…', pos: 'top', size: 48, color: 'white', anim: 'fade' }],
    effects: [{ type: 'varCard', text: 'CHUẨN!', start: 1.5, end: 3.5 }] },
];

// Lấy thông báo lỗi RÕ NHẤT từ response (message/detail/title) để nói đúng lý do thất bại.
const errMsg = (e: any, fallback: string): string =>
  e?.response?.data?.message ||
  e?.response?.data?.detail ||
  e?.response?.data?.title ||
  e?.message ||
  fallback;

// Định nghĩa Ở MODULE SCOPE (không đặt trong StudioPage) để identity ổn định qua mỗi render.
// Nếu để trong component, mỗi lần setState (vd chọn nhạc) sẽ tạo hàm mới → React remount cả subtree
// → scroll container "Kho nhạc" bị dựng lại, cuộn về đầu ("nhảy lung tung" khi chọn).
const Section = ({ icon, title, action, children }: any) => (
  <div className="ui-card rounded-xl p-4 mb-4">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 font-semibold ui-text">
        {icon} {title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const getDuration = (file: File): Promise<number> =>
  new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => resolve(v.duration || 0);
    v.onerror = () => resolve(0);
    v.src = URL.createObjectURL(file);
  });

const StudioPage = () => {
  const profile = useSelector((s: RootState) => s.features.auth.profile);
  const navigate = useNavigate();
  const clipInput = useRef<HTMLInputElement>(null);
  const musicInput = useRef<HTMLInputElement>(null);
  // QA S-25: cờ mounted để dừng vòng poll khi rời trang (tránh set state trên component đã unmount + poll vô hạn).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Hạng CAB+ (Pro): server gate thật; FE chỉ nới trần + hiển thị thông điệp cho khớp.
  const [isPremium, setIsPremium] = useState(false);
  useEffect(() => { getCabPlusStatus().then((s) => setIsPremium(!!s.isPremium)).catch(() => undefined); }, []);
  const maxTotal = isPremium ? 300 : MAX_TOTAL;

  const [clips, setClips] = useState<Clip[]>([]);
  const [music, setMusic] = useState<File | null>(null);
  const [musicVolume, setMusicVolume] = useState(0.35);
  // Voice AI (CAB+): thuyết minh tự động. Tạo blob mp3 rồi ghép như 1 track riêng khi xuất.
  const [voiceText, setVoiceText] = useState('');
  const [voiceId, setVoiceId] = useState(VOICE_OPTIONS[0].id);
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);
  const [voiceVolume, setVoiceVolume] = useState(1.0);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const voicePreviewRef = useRef<HTMLAudioElement | null>(null);
  const [voiceQuota, setVoiceQuota] = useState<VoiceQuota | null>(null);
  const [showVoicePacks, setShowVoicePacks] = useState(false);
  const [buyingPack, setBuyingPack] = useState<string | null>(null);
  const loadVoiceQuota = () => getVoiceQuota().then(setVoiceQuota).catch(() => undefined);
  useEffect(() => { loadVoiceQuota(); }, []);
  const [muteOriginal, setMuteOriginal] = useState(true); // tắt tiếng gốc clip khi có nhạc nền (tránh chồng tiếng)
  const [autoCaptions, setAutoCaptions] = useState(false); // phụ đề tự động (Whisper) — chỉ CAB+
  const [trimSilence, setTrimSilence] = useState(false); // cắt khoảng lặng (AI trim) — mọi user
  const [hlLoading, setHlLoading] = useState<number | null>(null); // clip đang phân tích highlight
  const [hlRanges, setHlRanges] = useState<Record<number, { start: number; end: number }[]>>({});
  // Kho nhạc free (Pixabay). Chọn 1 bài (libId) HOẶC tự upload (music) — loại trừ nhau.
  const [libTracks, setLibTracks] = useState<MusicTrack[]>([]);
  const [libCats, setLibCats] = useState<MusicCategory[]>([]);
  const [libCat, setLibCat] = useState<string>('all'); // danh mục đang lọc ('all' = tất cả)
  const [libId, setLibId] = useState<string | null>(null);
  const [showLib, setShowLib] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const musicUrlCache = useRef<Record<string, string>>({}); // cache URL nghe thử → bấm lại không tải lại (đỡ lag)
  const [texts, setTexts] = useState<TextItem[]>([]);
  const [content, setContent] = useState('');
  const [trimIdx, setTrimIdx] = useState<number | null>(null); // clip đang mở modal cắt

  const [composing, setComposing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [result, setResult] = useState<ComposeResult | null>(null);
  // Thẻ mua sắm tương tác (Interactive Hotspots) gắn vào video khi đăng.
  const [hotspots, setHotspots] = useState<{ label: string; url: string; start: number; end: number }[]>([]);
  const [effects, setEffects] = useState<EffectItem[]>([]); // hiệu ứng động (từ template hoặc tự thêm)
  const [transition, setTransition] = useState(''); // ① chuyển cảnh giữa clip ('' = cắt cứng)
  const [colorFilter, setColorFilter] = useState(''); // ② bộ lọc màu ('' = gốc)
  const [motion, setMotion] = useState(''); // ④ zoom chuyển động ('' = tắt)
  const [trend, setTrend] = useState(''); // ⑤ gói trend ('' = tắt)
  // Khung hình mẫu lấy từ clip của user -> dùng cho các ô xem trước hiệu ứng.
  const sampleFrames = useSampleFrames(clips);
  // Khối đang chọn trên bàn dựng (dùng chung giữa timeline và các form bên dưới).
  const [selText, setSelText] = useState<number | null>(null);
  const [selEffect, setSelEffect] = useState<number | null>(null);

  // Timeline LUÔN hiện: hễ có clip là tự chọn clip đầu (trước đây phải bấm "Cắt" mới thấy dải phim).
  useEffect(() => {
    if (clips.length === 0) {
      if (trimIdx !== null) setTrimIdx(null);
    } else if (trimIdx === null || trimIdx >= clips.length) {
      setTrimIdx(0);
    }
  }, [clips.length, trimIdx]);
  const [introTitle, setIntroTitle] = useState(''); const [introSub, setIntroSub] = useState(''); // ⑥ bìa mở đầu
  const [outroTitle, setOutroTitle] = useState(''); const [outroSub, setOutroSub] = useState(''); // ⑥ bìa kết
  const [karaoke, setKaraoke] = useState(false); // ⑥ phụ đề karaoke (cần bật phụ đề tự động - CAB+)
  const addHotspot = () => setHotspots((p) => [...p, { label: '', url: '', start: 0, end: Math.min(result?.duration || 5, 5) }]);
  const updHotspot = (i: number, patch: Partial<{ label: string; url: string; start: number; end: number }>) =>
    setHotspots((p) => p.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const rmHotspot = (i: number) => setHotspots((p) => p.filter((_, idx) => idx !== i));
  const [posting, setPosting] = useState(false);

  const total = useMemo(
    () => clips.reduce((s, c) => s + Math.max(0, c.end - c.start), 0),
    [clips]
  );

  // Tải kho nhạc lần đầu mở; dừng preview khi rời trang.
  useEffect(() => {
    if (showLib && libTracks.length === 0) {
      getMusicLibrary()
        .then((d) => {
          setLibTracks(d.tracks || []);
          setLibCats(d.categories || []);
        })
        .catch(() => message.error('Không tải được kho nhạc.'));
    }
  }, [showLib, libTracks.length]);
  useEffect(() => () => previewRef.current?.pause(), []);

  // Nghe thử phải đúng âm lượng đã chọn → chỉnh slider thấy/nghe được ngay khi đang phát.
  useEffect(() => {
    if (previewRef.current) previewRef.current.volume = musicVolume;
  }, [musicVolume]);

  const togglePreview = async (id: string) => {
    if (playingId === id) {
      previewRef.current?.pause();
      setPlayingId(null);
      return;
    }
    try {
      const url = musicUrlCache.current[id] || (await getMusicPreviewUrl(id));
      musicUrlCache.current[id] = url; // cache → lần sau bấm phát ngay, đỡ lag
      if (!previewRef.current) previewRef.current = new Audio();
      previewRef.current.src = url;
      previewRef.current.volume = musicVolume; // nghe đúng mức âm lượng nền sẽ trộn vào video
      previewRef.current.onended = () => setPlayingId(null);
      await previewRef.current.play();
      setPlayingId(id);
    } catch {
      message.error('Không nghe thử được bài này.');
    }
  };

  // Chọn nhạc từ kho → bỏ nhạc tự upload (loại trừ nhau). Nhạc cao cấp (VIP) chỉ CAB+ mới chọn được.
  const pickLibTrack = (id: string) => {
    const t = libTracks.find((x) => x.id === id);
    if (t?.isVip && !isPremium) {
      message.warning('Nhạc cao cấp chỉ dành cho tài khoản CAB+. Nâng cấp để mở khóa kho nhạc này.');
      return;
    }
    setLibId((prev) => (prev === id ? null : id));
    setMusic(null);
  };
  const libSelected = libTracks.find((t) => t.id === libId) || null;

  // Giới hạn dung lượng/clip theo hạng (khớp server): Creator 60MB, mở donate 35MB, thường 15MB.
  const userMaxMb =
    profile?.userType === 'CONTENT_CREATOR'
      ? 60
      : (profile as any)?.canReceiveDonation
      ? 35
      : 15;

  const addClips = async (files: FileList | null) => {
    if (!files) return;
    const room = MAX_CLIPS - clips.length;
    const picked = Array.from(files).slice(0, room);
    const added: Clip[] = [];
    for (const file of picked) {
      const mb = file.size / (1024 * 1024);
      if (mb > userMaxMb) {
        message.error(
          `"${file.name}" nặng ${mb.toFixed(1)}MB, vượt giới hạn ${userMaxMb}MB của tài khoản. Hãy chọn clip nhẹ hơn hoặc cắt ngắn.`
        );
        continue;
      }
      const duration = await getDuration(file);
      added.push({ file, url: URL.createObjectURL(file), duration, start: 0, end: duration });
    }
    setClips((prev) => [...prev, ...added]);
  };

  const updateClip = (i: number, patch: Partial<Clip>) =>
    setClips((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  // Highlight AI: phân tích clip → gợi ý khoảng hấp dẫn; bấm 1 gợi ý → set start/end cho clip.
  const runHighlights = async (i: number) => {
    try {
      setHlLoading(i);
      const ranges = await getHighlights(clips[i].file);
      setHlRanges((prev) => ({ ...prev, [i]: ranges }));
      if (!ranges.length) message.info('Chưa tìm được khoảnh khắc nổi bật (clip ngắn hoặc ít biến động âm thanh).');
    } catch {
      message.error('Không phân tích được highlight, thử lại sau.');
    } finally { setHlLoading(null); }
  };
  const applyHighlight = (i: number, r: { start: number; end: number }) => {
    updateClip(i, { start: r.start, end: Math.min(r.end, clips[i].duration) });
    message.success(`Đã áp khoảnh khắc ${r.start.toFixed(1)}s–${r.end.toFixed(1)}s cho clip ${i + 1}.`);
  };
  const removeClip = (i: number) => setClips((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) =>
    setClips((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const addText = () =>
    setTexts((prev) => [
      ...prev,
      { text: '', pos: 'bottom', size: 42, color: 'white', start: 0, end: Math.min(total || 5, 5) },
    ]);
  const updateText = (i: number, patch: Partial<TextItem>) =>
    setTexts((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const removeText = (i: number) => {
    setTexts((prev) => prev.filter((_, idx) => idx !== i));
    setSelText(null); // chỉ số dịch sau khi xoá -> bỏ chọn cho khỏi sáng sai dòng
  };

  const applyPreset = (p: StudioPreset) => {
    const end = Math.min(total || 5, 5);
    setTexts(p.texts.map((t) => ({ ...t, start: 0, end, anim: t.anim || '' })));
    setEffects(
      (p.effects || []).map((e) => ({
        type: e.type,
        text: e.text ?? EFFECT_META[e.type].defText,
        color: e.color ?? EFFECT_META[e.type].defColor,
        start: e.start ?? 0,
        end: e.end ?? end,
      }))
    );
    setMuteOriginal(p.mute);
    setTransition(p.transition ?? ''); // ① chuyển cảnh theo mẫu
    setColorFilter(p.colorFilter ?? ''); // ② bộ lọc màu theo mẫu
    setMotion(p.motion ?? ''); // ④ zoom động theo mẫu
    setTrend(p.trend ?? ''); // ⑤ trend theo mẫu
    message.success(`Đã áp mẫu "${p.name}" — sửa nội dung chữ/hiệu ứng & chọn nhạc phù hợp (${p.hint}).`);
  };

  const addEffect = (type: EffectType) => {
    const m = EFFECT_META[type];
    setEffects((prev) => [...prev, { type, text: m.defText, color: m.defColor, start: 0, end: Math.min(total || 5, 5) }]);
  };
  const updateEffect = (i: number, patch: Partial<EffectItem>) =>
    setEffects((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const removeEffect = (i: number) => {
    setEffects((prev) => prev.filter((_, idx) => idx !== i));
    setSelEffect(null);
  };

  const genVoice = async () => {
    const text = voiceText.trim();
    if (!text) return message.warning('Nhập nội dung cần đọc.');
    if (text.length > 3000) return message.warning('Đoạn quá dài (tối đa 3.000 ký tự).');
    try {
      setVoiceLoading(true);
      const { blob } = await synthesizeVoice(text, voiceId, voiceSpeed);
      setVoiceBlob(blob);
      if (voicePreviewRef.current) {
        voicePreviewRef.current.src = URL.createObjectURL(blob);
        voicePreviewRef.current.volume = Math.min(1, voiceVolume);
        voicePreviewRef.current.play().catch(() => undefined);
      }
      message.success('Đã tạo giọng đọc — bấm ▶ để nghe thử.');
      loadVoiceQuota();
    } catch (e: any) {
      if (e?.response?.status === 402) {
        message.warning(e?.response?.data?.message || 'Bạn đã hết ký tự voice — mua thêm gói để tiếp tục.');
        setShowVoicePacks(true);
        loadVoiceQuota();
      } else {
        message.error('Không tạo được giọng đọc. Thử lại sau.');
      }
    } finally {
      setVoiceLoading(false);
    }
  };

  const doBuyVoicePack = async (packId: string) => {
    setBuyingPack(packId);
    try {
      const r = await buyVoicePack(packId, getAffRef());
      if (r.success) {
        message.success(r.message || 'Đã mua gói ký tự voice.');
        setShowVoicePacks(false);
        loadVoiceQuota();
      } else {
        message.warning(r.message || 'Mua gói thất bại.');
      }
    } catch {
      message.error('Mua gói thất bại. Thử lại sau.');
    } finally {
      setBuyingPack(null);
    }
  };

  const compose = async () => {
    if (clips.length === 0) return message.warning('Thêm ít nhất 1 clip.');
    if (total <= 0) return message.warning('Khoảng cắt chưa hợp lệ.');
    if (total > maxTotal) return message.warning(`Tổng thời lượng ${total.toFixed(1)}s vượt ${maxTotal}s${isPremium ? '' : ' — nâng CAB+ để mở tới 5 phút'}.`);

    const fd = new FormData();
    clips.forEach((c, i) => fd.append(`clip${i}`, c.file, c.file.name));
    fd.append('clips', JSON.stringify(clips.map((c) => ({ start: c.start, end: c.end }))));
    if (music) {
      fd.append('music', music, music.name);
      fd.append('musicVolume', String(musicVolume));
    } else if (libId) {
      fd.append('musicId', libId);
      fd.append('musicVolume', String(musicVolume));
    }
    if (music || libId) fd.append('muteOriginal', String(muteOriginal));
    if (voiceBlob) { // Voice AI: thuyết minh đã tạo → ghép như track riêng
      fd.append('voice', voiceBlob, 'voice.mp3');
      fd.append('voiceVolume', String(voiceVolume));
    }
    if (isPremium && autoCaptions) fd.append('autoCaptions', 'true'); // phụ đề tự động — chỉ gửi khi CAB+ bật
    if (trimSilence) fd.append('trimSilence', 'true'); // cắt khoảng lặng
    const validTexts = texts.filter((t) => t.text.trim());
    if (validTexts.length) {
      fd.append(
        'texts',
        JSON.stringify(
          validTexts.map((t) => ({
            text: t.text,
            x: 0.5,
            y: POS_Y[t.pos],
            size: t.size,
            color: t.color,
            start: t.start,
            end: t.end,
            anim: t.anim || '',
          }))
        )
      );
    }
    const validEffects = effects.filter((e) => e.text.trim());
    if (validEffects.length) {
      fd.append('effects', JSON.stringify(validEffects)); // hiệu ứng động: {type,text,color,start,end}
    }
    if (transition) fd.append('transition', transition); // ① chuyển cảnh giữa clip
    if (colorFilter) fd.append('colorFilter', colorFilter); // ② bộ lọc màu
    if (motion) fd.append('motion', motion); // ④ zoom chuyển động
    if (trend) fd.append('trend', trend); // ⑤ gói trend
    if (introTitle.trim()) fd.append('introTitle', introTitle.trim()); // ⑥ bìa mở đầu
    if (introSub.trim()) fd.append('introSub', introSub.trim());
    if (outroTitle.trim()) fd.append('outroTitle', outroTitle.trim()); // ⑥ bìa kết
    if (outroSub.trim()) fd.append('outroSub', outroSub.trim());
    if (isPremium && autoCaptions && karaoke) fd.append('karaoke', 'true'); // ⑥ karaoke (chỉ khi phụ đề tự động bật)

    setComposing(true);
    setResult(null);
    setProgress(0);
    setStage('Đang gửi…');
    try {
      const { jobId } = await startCompose(fd);
      // Poll tiến độ mỗi 2 giây cho tới khi xong/lỗi.
      // QA S-25: dừng khi rời trang (mountedRef) + trần thời gian ~15 phút để job kẹt không poll vô hạn.
      const maxAttempts = 450; // 450 * 2s = 15 phút
      let attempts = 0;
      while (mountedRef.current && attempts < maxAttempts) {
        attempts += 1;
        await new Promise((r) => setTimeout(r, 2000));
        if (!mountedRef.current) return;
        const st = await getJobStatus(jobId);
        if (!mountedRef.current) return;
        setProgress(st.progress || 0);
        setStage(st.stage || '');
        if (st.status === 'Done' && st.result?.url) {
          setResult(st.result);
          message.success('Xuất video thành công!');
          break;
        }
        if (st.status === 'Failed') {
          message.error(st.error || 'Xuất video thất bại.');
          break;
        }
      }
      if (attempts >= maxAttempts && mountedRef.current) {
        message.error('Quá thời gian xử lý. Vui lòng thử lại sau.');
      }
    } catch (e: any) {
      if (mountedRef.current) message.error(errMsg(e, 'Xuất video thất bại.'));
    } finally {
      if (mountedRef.current) setComposing(false);
    }
  };

  const postVideo = async () => {
    if (!result) return;
    setPosting(true);
    try {
      // CC-BY: tự thêm dòng ghi công vào caption (tuân thủ giấy phép) nếu dùng nhạc kho cần ghi công.
      const credit = libSelected?.credit;
      const finalContent =
        credit && !content.includes(credit) ? `${content}${content ? '\n\n' : ''}${credit}` : content;
      await createNewPost({
        userId: profile?.userId,
        postType: 'video',
        content: finalContent,
        title: finalContent,
        hashtags: '',
        imageInfo: [],
        videoInfo: [{ videoId: result.id, videoUrl: result.url }],
        videoHotspots: hotspots.length ? JSON.stringify(hotspots) : undefined,
      });
      message.success('Đã đăng video!');
      navigate('/');
    } catch (e: any) {
      message.error(`Đăng bài thất bại: ${errMsg(e, 'lỗi không xác định, thử lại sau.')}`);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-3 py-4">
      <h1 className="text-2xl font-bold mb-1 ui-heading">Cab Studio</h1>
      <p className="ui-muted text-sm mb-2">
        Cắt, ghép, thêm nhạc & chữ cho video (tối đa {MAX_CLIPS} clip, {maxTotal} giây, mỗi clip ≤{' '}
        {userMaxMb}MB).
      </p>
      <div className={`text-xs mb-5 rounded-lg px-3 py-2 border ${isPremium ? 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800' : 'ui-divider ui-muted'}`}>
        {isPremium
          ? '👑 CAB+ Pro: video tới 5 phút · xuất 1080p · KHÔNG watermark.'
          : 'Hạng Free: tối đa 60 giây · 720p · có watermark cab.vn. Nâng CAB+ để mở 5 phút, 1080p và bỏ watermark.'}
      </div>

      {/* Template Hub — mẫu dựng nhanh: bấm 1 phát prefill bố cục chữ + gợi ý cách dùng */}
      <div className="ui-card rounded-xl p-4 mb-4">
        <div className="font-semibold ui-text mb-1">Mẫu dựng nhanh</div>
        <p className="ui-muted text-xs mb-3">Chọn 1 mẫu để điền sẵn bố cục chữ — bạn chỉ cần sửa nội dung & chọn nhạc.</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              className="rounded-lg ui-divider border px-3 py-2 text-sm ui-text hover:border-[#0284c7] hover:text-[#0284c7] transition text-left"
              title={p.hint}
            >
              <span className="mr-1">{p.emoji}</span>{p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Clips */}
      <Section
        icon={<Film size={18} />}
        title={`Clip (${clips.length}/${MAX_CLIPS}) · ${total.toFixed(1)}s`}
        action={
          <Button
            icon={<Plus size={14} />}
            size="small"
            disabled={clips.length >= MAX_CLIPS}
            onClick={() => clipInput.current?.click()}
          >
            Thêm clip
          </Button>
        }
      >
        <input
          ref={clipInput}
          type="file"
          accept="video/*"
          multiple
          hidden
          onChange={(e) => {
            addClips(e.target.files);
            e.target.value = '';
          }}
        />
        {clips.length === 0 && <p className="ui-muted text-sm">Chưa có clip nào.</p>}
        <div className="flex flex-col gap-3">
          {clips.map((c, i) => (
            <div
              key={i}
              className={`flex gap-3 items-start ui-surface-2 rounded-lg p-2 border ${
                i === trimIdx ? 'border-[#0284c7]' : 'border-transparent'
              }`}
            >
              <button
                type="button"
                onClick={() => setTrimIdx(i)}
                title="Cắt clip này"
                className="shrink-0"
              >
                <video src={c.url} className="w-24 h-32 object-cover rounded bg-black" muted />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm ui-text mb-1 truncate">
                  Clip {i + 1} · {c.duration.toFixed(1)}s
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="small"
                    icon={<Scissors size={14} />}
                    type={i === trimIdx ? 'primary' : 'default'}
                    onClick={() => setTrimIdx(i)}
                  >
                    Cắt
                  </Button>
                  <Button size="small" loading={hlLoading === i} onClick={() => runHighlights(i)}>
                    ✨ Gợi ý khoảnh khắc
                  </Button>
                  <span className="text-xs ui-muted tabular-nums">
                    Giữ {c.start.toFixed(1)}s → {c.end.toFixed(1)}s ({(c.end - c.start).toFixed(1)}s)
                  </span>
                </div>
                {(hlRanges[i]?.length ?? 0) > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    <span className="text-xs ui-muted">Gợi ý:</span>
                    {hlRanges[i].map((r, k) => (
                      <button
                        key={k}
                        onClick={() => applyHighlight(i, r)}
                        className="text-xs rounded-full border border-[#0284c7] text-[#0284c7] px-2 py-0.5 hover:bg-[#0284c7]/10"
                      >
                        {r.start.toFixed(1)}s–{r.end.toFixed(1)}s
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(i, -1)} className="ui-muted hover:ui-text" title="Lên">
                  <ArrowUp size={16} />
                </button>
                <button onClick={() => move(i, 1)} className="ui-muted hover:ui-text" title="Xuống">
                  <ArrowDown size={16} />
                </button>
                <button onClick={() => removeClip(i)} className="text-red-500" title="Xoá">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Bàn cắt — dải phim LUÔN HIỆN cho clip đang chọn (không còn phải bấm "Cắt" mở popup).
          Bấm vào clip ở danh sách trên để đổi clip đang cắt. */}
      {trimIdx !== null && clips[trimIdx] && (
        <Section
          icon={<Scissors size={18} />}
          title={`Bàn dựng · đang cắt Clip ${trimIdx + 1}/${clips.length}`}
          action={
            clips.length > 1 ? (
              <div className="flex gap-1">
                {clips.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTrimIdx(i)}
                    className={`w-7 h-7 rounded text-xs border ${
                      i === trimIdx
                        ? 'border-[#0284c7] text-[#0284c7] font-medium'
                        : 'ui-divider ui-muted hover:ui-text'
                    }`}
                    title={`Cắt clip ${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            ) : undefined
          }
        >
          {/* Lớp trên: toàn cảnh nhiều lớp trên cùng trục thời gian (kéo thả chữ/hiệu ứng). */}
          <Timeline
            total={total}
            clips={clips}
            selectedClip={trimIdx}
            onSelectClip={setTrimIdx}
            texts={texts}
            selectedText={selText}
            onSelectText={setSelText}
            onChangeText={updateText}
            effects={effects}
            selectedEffect={selEffect}
            onSelectEffect={setSelEffect}
            onChangeEffect={updateEffect}
            effectLabel={(t) => EFFECT_META[t as EffectType]?.label ?? 'Hiệu ứng'}
            musicLabel={music ? music.name : libSelected ? libSelected.title : null}
            voiceLabel={voiceBlob ? 'Giọng đọc' : null}
          />

          <div className="h-px ui-divider border-t my-3" />

          {/* Lớp dưới: cắt tinh clip đang chọn trên dải phim. */}
          <VideoTrimmer
            url={clips[trimIdx].url}
            duration={clips[trimIdx].duration}
            start={clips[trimIdx].start}
            end={clips[trimIdx].end}
            onChange={(st, en) => updateClip(trimIdx, { start: st, end: en })}
          />
          <p className="text-[11px] ui-muted mt-2">
            Kéo 2 mép dải phim để chọn đoạn giữ lại của Clip {trimIdx + 1}.
          </p>
        </Section>
      )}

      {/* Music */}
      <Section
        icon={<Music size={18} />}
        title="Nhạc nền"
        action={
          <div className="flex gap-2">
            <Button
              size="small"
              icon={<ListMusic size={14} />}
              type={showLib ? 'primary' : 'default'}
              onClick={() => setShowLib((v) => !v)}
            >
              Kho nhạc free
            </Button>
            <Button size="small" icon={<Plus size={14} />} onClick={() => musicInput.current?.click()}>
              Nhạc của tôi
            </Button>
          </div>
        }
      >
        <input
          ref={musicInput}
          type="file"
          accept="audio/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            setMusic(f);
            if (f) setLibId(null); // upload nhạc riêng → bỏ chọn kho
            e.target.value = '';
          }}
        />

        {/* Kho nhạc free — Pixabay + Kevin MacLeod, chia theo danh mục */}
        {showLib && (
          <div className="mb-3 rounded-lg ui-divider border p-2">
            <p className="text-xs ui-muted px-1 pb-1">
              Nhạc miễn phí bản quyền do Cab cung cấp (Pixabay + Kevin MacLeod). Bấm ▶ để nghe thử.
            </p>
            {/* Tab lọc theo danh mục */}
            {libCats.length > 0 && (
              <div className="flex flex-wrap gap-1 px-1 pb-2">
                <button
                  type="button"
                  onClick={() => setLibCat('all')}
                  className={`text-[11px] rounded-full px-2.5 py-1 border transition ${
                    libCat === 'all'
                      ? 'bg-sky-500 text-white border-sky-500'
                      : 'ui-muted ui-divider hover:bg-black/5 dark:hover:bg-white/5'
                  }`}
                >
                  Tất cả ({libTracks.length})
                </button>
                {libCats.map((c) => {
                  const n = libTracks.filter((t) => t.category === c.key).length;
                  if (!n) return null;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setLibCat(c.key)}
                      className={`text-[11px] rounded-full px-2.5 py-1 border transition ${
                        libCat === c.key
                          ? 'bg-sky-500 text-white border-sky-500'
                          : 'ui-muted ui-divider hover:bg-black/5 dark:hover:bg-white/5'
                      }`}
                    >
                      {c.label} ({n})
                    </button>
                  );
                })}
              </div>
            )}
            {/* Danh sách bài (vùng cuộn riêng) */}
            <div className="max-h-56 overflow-y-auto space-y-1">
            {libTracks.length === 0 ? (
              <p className="ui-muted text-sm px-1">Đang tải…</p>
            ) : (
              libTracks
                .filter((t) => libCat === 'all' || t.category === libCat)
                .map((t) => (
                <div
                  key={t.id}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer ${
                    libId === t.id ? 'bg-sky-500/15 ring-1 ring-sky-400' : 'hover:bg-black/5 dark:hover:bg-white/5'
                  } ${t.isVip && !isPremium ? 'opacity-60' : ''}`}
                  onClick={() => pickLibTrack(t.id)}
                >
                  <button
                    className="ui-text shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePreview(t.id);
                    }}
                  >
                    {playingId === t.id ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm ui-text truncate">
                      {t.title}
                      {t.isSfx && <span className="ml-1 text-[10px] ui-muted">(hiệu ứng)</span>}
                      {t.isVip && (
                        <span className="ml-1 align-middle inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-400">
                          {!isPremium && <Lock size={10} />} VIP
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] ui-muted truncate">{t.mood}</div>
                    {t.credit && (
                      <div className="text-[10px] text-amber-600 dark:text-amber-400 truncate" title={t.credit}>
                        © {t.author} · CC BY
                      </div>
                    )}
                  </div>
                  {libId === t.id && <span className="text-xs text-sky-500 shrink-0">✓ Đã chọn</span>}
                </div>
              ))
            )}
            </div>
          </div>
        )}

        {music || libSelected ? (
          <div className="flex items-center gap-3">
            <span className="text-sm ui-text truncate flex-1">
              {music ? `🎵 ${music.name}` : `🎵 ${libSelected?.title} (kho free)`}
            </span>
            <span className="text-xs ui-muted whitespace-nowrap">Âm lượng {Math.round(musicVolume * 100)}%</span>
            <Slider
              className="w-32"
              min={0}
              max={1}
              step={0.01}
              value={musicVolume}
              onChange={setMusicVolume}
              tooltip={{ formatter: (v) => `${Math.round((v || 0) * 100)}%` }}
            />
            <button
              className="text-red-500"
              onClick={() => {
                setMusic(null);
                setLibId(null);
              }}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ) : (
          <p className="ui-muted text-sm">Chưa có nhạc.</p>
        )}
        {(music || libSelected) && (
          <div className="mt-3 flex items-center gap-2">
            <Switch size="small" checked={muteOriginal} onChange={setMuteOriginal} />
            <span className="text-sm ui-text">Tắt tiếng gốc của clip</span>
            <span className="text-xs ui-muted">
              {muteOriginal ? '(chỉ phát nhạc nền)' : '(giữ tiếng gốc + trộn nhạc — hợp vlog có giọng nói)'}
            </span>
          </div>
        )}

        {/* Auto-caption (Whisper) — chỉ CAB+ */}
        <div className="mt-3 flex items-center gap-2">
          <Switch size="small" checked={isPremium && autoCaptions} disabled={!isPremium} onChange={setAutoCaptions} />
          <span className="text-sm ui-text">Tự động tạo phụ đề {!isPremium && <span className="text-amber-500 font-semibold">(CAB+)</span>}</span>
          <span className="text-xs ui-muted">
            {isPremium
              ? '(nghe lời thoại trong clip rồi tự chạy chữ dưới màn hình)'
              : '— nâng CAB+ để bật phụ đề tự động'}
          </span>
        </div>
        {isPremium && autoCaptions && (
          <div className="mt-2 flex items-center gap-2 pl-6">
            <Switch size="small" checked={karaoke} onChange={setKaraoke} />
            <span className="text-sm ui-text">Phụ đề kiểu karaoke</span>
            <span className="text-xs ui-muted">(chữ hiện dần theo từng từ khi được nói)</span>
          </div>
        )}

        {/* Cắt khoảng lặng (AI trim) — mọi user */}
        <div className="mt-3 flex items-center gap-2">
          <Switch size="small" checked={trimSilence} onChange={setTrimSilence} />
          <span className="text-sm ui-text">Tự động cắt khoảng lặng</span>
          <span className="text-xs ui-muted">(bỏ đoạn im tiếng để video gọn, cuốn hơn)</span>
        </div>
      </Section>

      {/* Voice AI — thuyết minh tự động */}
      <Section icon={<Mic size={18} />} title="Giọng đọc">
        <p className="ui-muted text-sm mb-2">
          Bạn nhập nội dung, Cab đọc thành giọng thuyết minh rồi ghép sẵn vào video.
        </p>
        {/* Hạn mức ký tự: free tháng (+CAB+) + gói đã mua */}
        {voiceQuota && (
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs rounded-lg bg-slate-500/5 px-3 py-2">
            <span className="ui-text font-medium">
              Còn <span className="text-emerald-600 dark:text-emerald-400">{voiceQuota.totalRemaining.toLocaleString('vi-VN')}</span> ký tự
            </span>
            <span className="ui-muted">
              (tháng này {voiceQuota.monthlyRemaining.toLocaleString('vi-VN')}
              {voiceQuota.isPremium ? ' · đã gồm CAB+' : ''}
              {voiceQuota.packChars > 0 ? ` + gói ${voiceQuota.packChars.toLocaleString('vi-VN')}` : ''})
            </span>
            <button className="text-sky-500 hover:underline font-medium" onClick={() => setShowVoicePacks(true)}>
              Mua thêm ký tự
            </button>
          </div>
        )}
        <Input.TextArea
          rows={3}
          maxLength={3000}
          showCount
          placeholder="Nhập lời thuyết minh cần đọc…"
          value={voiceText}
          onChange={(e) => { setVoiceText(e.target.value); setVoiceBlob(null); }}
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Select
            size="small"
            value={voiceId}
            style={{ width: 200 }}
            onChange={(v) => { setVoiceId(v); setVoiceBlob(null); }}
            options={VOICE_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
          />
          <span className="text-xs ui-muted">Tốc độ</span>
          <Select
            size="small"
            value={voiceSpeed}
            style={{ width: 90 }}
            onChange={(v) => { setVoiceSpeed(v); setVoiceBlob(null); }}
            options={[
              { value: 0.75, label: '0.75x' },
              { value: 1.0, label: '1.0x' },
              { value: 1.25, label: '1.25x' },
              { value: 1.5, label: '1.5x' },
            ]}
          />
          <Button size="small" type="primary" loading={voiceLoading} disabled={!voiceText.trim()} onClick={genVoice}>
            Tạo giọng đọc{voiceText.trim() ? ` (${voiceText.trim().length} ký tự)` : ''}
          </Button>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio ref={voicePreviewRef} controls className="h-8" style={{ display: voiceBlob ? 'inline-block' : 'none' }} />
        </div>
        {voiceBlob && (
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm ui-text truncate flex-1">🎙️ Đã có giọng đọc — sẽ ghép khi xuất video</span>
            <span className="text-xs ui-muted whitespace-nowrap">Âm lượng {Math.round(voiceVolume * 100)}%</span>
            <Slider
              className="w-32"
              min={0}
              max={1.5}
              step={0.01}
              value={voiceVolume}
              onChange={(v) => { setVoiceVolume(v); if (voicePreviewRef.current) voicePreviewRef.current.volume = Math.min(1, v); }}
              tooltip={{ formatter: (v) => `${Math.round((v || 0) * 100)}%` }}
            />
            <button className="text-red-500" onClick={() => setVoiceBlob(null)}>
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </Section>

      {/* Modal mua gói ký tự voice */}
      <Modal
        open={showVoicePacks}
        onCancel={() => !buyingPack && setShowVoicePacks(false)}
        footer={null}
        centered
        width={420}
        title="Mua thêm ký tự Giọng đọc"
      >
        <p className="ui-muted text-sm mb-1">
          Ký tự đã mua không hết hạn, dùng dần sau khi hết hạn mức tháng.
        </p>
        {voiceQuota && (
          <p className="text-xs ui-muted mb-3">
            Số dư: <b className="ui-text">{voiceQuota.balanceCa.toLocaleString('vi-VN')} Ca</b> · Còn {voiceQuota.totalRemaining.toLocaleString('vi-VN')} ký tự
          </p>
        )}
        <div className="flex flex-col gap-2">
          {(voiceQuota?.packs || []).map((p) => {
            const notEnough = !!voiceQuota && voiceQuota.balanceCa < p.priceCa;
            return (
              <div key={p.id} className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex-1">
                  <div className="ui-text font-semibold">{p.label}</div>
                  <div className="ui-muted text-xs">{p.priceCa.toLocaleString('vi-VN')} Ca</div>
                </div>
                <Button
                  type="primary"
                  loading={buyingPack === p.id}
                  disabled={notEnough || !!buyingPack}
                  onClick={() => doBuyVoicePack(p.id)}
                >
                  {notEnough ? 'Không đủ Ca' : 'Mua'}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] ui-muted mt-3">
          Hoặc nâng <b>CAB+</b> để được kèm {voiceQuota ? voiceQuota.cabPlusPerMonth.toLocaleString('vi-VN') : ''} ký tự mỗi tháng.
        </p>
      </Modal>

      {/* Text */}
      <Section
        icon={<Type size={18} />}
        title="Chữ"
        action={
          <Button size="small" icon={<Plus size={14} />} onClick={addText}>
            Thêm chữ
          </Button>
        }
      >
        {texts.length === 0 && <p className="ui-muted text-sm">Chưa có chữ.</p>}
        <div className="flex flex-col gap-2">
          {texts.map((t, i) => (
            <div
              key={i}
              onPointerDown={() => setSelText(i)}
              className={`flex flex-wrap items-center gap-2 ui-surface-2 rounded-lg p-2 border ${
                selText === i ? 'border-[#7c3aed]' : 'border-transparent'
              }`}
            >
              <Input
                className="flex-1 min-w-[140px]"
                placeholder="Nội dung chữ"
                value={t.text}
                onChange={(e) => updateText(i, { text: e.target.value })}
              />
              <Select
                size="small"
                value={t.pos}
                style={{ width: 90 }}
                onChange={(v) => updateText(i, { pos: v })}
                options={[
                  { value: 'top', label: 'Trên' },
                  { value: 'center', label: 'Giữa' },
                  { value: 'bottom', label: 'Dưới' },
                ]}
              />
              <Select
                size="small"
                value={t.color}
                style={{ width: 90 }}
                onChange={(v) => updateText(i, { color: v })}
                options={['white', 'yellow', 'red', 'black'].map((c) => ({ value: c, label: c }))}
              />
              <InputNumber
                size="small"
                min={10}
                max={120}
                value={t.size}
                onChange={(v) => updateText(i, { size: Number(v) || 42 })}
              />
              <Select
                size="small"
                value={t.anim || ''}
                style={{ width: 110 }}
                onChange={(v) => updateText(i, { anim: v })}
                options={[
                  { value: '', label: 'Chữ tĩnh' },
                  { value: 'fade', label: 'Mờ dần' },
                  { value: 'slidein', label: 'Bay vào' },
                  { value: 'slideup', label: 'Trượt lên' },
                ]}
              />
              <span className="text-xs ui-muted">Hiện từ giây</span>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                min={0}
                max={Math.max(0, total)}
                step={0.1}
                value={t.start}
                onChange={(v) => updateText(i, { start: Math.max(0, Number(v) || 0) })}
              />
              <span className="text-xs ui-muted">đến</span>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                min={0}
                max={Math.max(0, total)}
                step={0.1}
                value={t.end}
                onChange={(v) => updateText(i, { end: Math.max(t.start, Number(v) || 0) })}
              />
              <button className="text-red-500" onClick={() => removeText(i)}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* Chuyển cảnh + màu + hiệu ứng — CHỌN BẰNG HÌNH, xem trước ngay trên clip của user.
          Trước đây là 4 dropdown chữ nên user không hình dung được sẽ ra thế nào. */}
      <Section icon={<Film size={18} />} title="Chuyển cảnh & Màu">
        {clips.length === 0 && (
          <p className="ui-muted text-sm mb-3">
            Thêm clip ở trên để xem trước hiệu ứng ngay trên video của bạn.
          </p>
        )}

        <div className="mb-4">
          <div className="text-sm ui-text font-medium mb-1.5">Bộ lọc màu</div>
          <FilterPicker value={colorFilter} onChange={setColorFilter} frame={sampleFrames.a} />
        </div>

        <div className="mb-4">
          <div className="text-sm ui-text font-medium mb-1.5">
            Chuyển cảnh giữa clip <span className="text-xs ui-muted font-normal">(cần từ 2 clip)</span>
          </div>
          <TransitionPicker
            value={transition}
            onChange={setTransition}
            frameA={sampleFrames.a}
            frameB={sampleFrames.b}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-sm ui-text font-medium mb-1.5">Zoom động</div>
            <MotionPicker value={motion} onChange={setMotion} frame={sampleFrames.a} />
          </div>
          <div>
            <div className="text-sm ui-text font-medium mb-1.5">Hiệu ứng trend</div>
            <TrendPicker value={trend} onChange={setTrend} frame={sampleFrames.a} />
          </div>
        </div>

        <p className="text-[11px] ui-muted mt-3">
          Ô xem trước là mô phỏng để bạn hình dung — bản xuất cuối do máy chủ dựng nên có thể mượt hơn một chút.
        </p>
      </Section>

      {/* ⑥ Bìa mở đầu / kết (title card) */}
      <Section icon={<Type size={18} />} title="Bìa mở đầu / kết">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-sm ui-text font-medium mb-1">Bìa mở đầu</div>
            <Input placeholder="Tiêu đề (vd: Chào mừng!)" value={introTitle} onChange={(e) => setIntroTitle(e.target.value)} maxLength={80} />
            <Input className="mt-2" placeholder="Phụ đề (không bắt buộc)" value={introSub} onChange={(e) => setIntroSub(e.target.value)} maxLength={100} />
          </div>
          <div>
            <div className="text-sm ui-text font-medium mb-1">Bìa kết</div>
            <Input placeholder="Tiêu đề (vd: Cảm ơn đã xem!)" value={outroTitle} onChange={(e) => setOutroTitle(e.target.value)} maxLength={80} />
            <Input className="mt-2" placeholder="Phụ đề (vd: Đăng ký kênh nhé)" value={outroSub} onChange={(e) => setOutroSub(e.target.value)} maxLength={100} />
          </div>
        </div>
        <p className="text-[11px] ui-muted mt-2">Bỏ trống thì không thêm bìa. Mỗi bìa ~2,5 giây (nền tối, chữ căn giữa).</p>
      </Section>

      {/* Hiệu ứng động cầu kỳ — cháy vào video (render server-side). Template tự thêm; user sửa/thêm/bớt. */}
      <Section
        icon={<Sparkles size={18} />}
        title="Hiệu ứng động"
        action={
          <Select
            size="small"
            placeholder="+ Thêm hiệu ứng"
            style={{ width: 180 }}
            value={null as any}
            onChange={(v) => v && addEffect(v as EffectType)}
            options={(Object.keys(EFFECT_META) as EffectType[]).map((k) => ({
              value: k,
              label: `${EFFECT_META[k].emoji} ${EFFECT_META[k].label}`,
            }))}
          />
        }
      >
        {effects.length === 0 && (
          <p className="ui-muted text-sm">Chưa có hiệu ứng. Chọn một mẫu ở trên (Thể thao / Review / Meme) hoặc thêm thủ công.</p>
        )}
        <div className="flex flex-col gap-2">
          {effects.map((e, i) => (
            <div
              key={i}
              onPointerDown={() => setSelEffect(i)}
              className={`flex flex-wrap items-center gap-2 ui-surface-2 rounded-lg p-2 border ${
                selEffect === i ? 'border-[#db2777]' : 'border-transparent'
              }`}
            >
              <span className="text-sm font-medium ui-text shrink-0" style={{ minWidth: 150 }}>
                {EFFECT_META[e.type].emoji} {EFFECT_META[e.type].label}
              </span>
              <Input
                className="flex-1 min-w-[120px]"
                placeholder="Nội dung chữ trên hiệu ứng"
                value={e.text}
                onChange={(ev) => updateEffect(i, { text: ev.target.value })}
              />
              <span className="text-xs ui-muted">Hiện từ giây</span>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                min={0}
                max={Math.max(0, total)}
                step={0.1}
                value={e.start}
                onChange={(v) => updateEffect(i, { start: Math.max(0, Number(v) || 0) })}
              />
              <span className="text-xs ui-muted">đến</span>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                min={0}
                max={Math.max(0, total)}
                step={0.1}
                value={e.end}
                onChange={(v) => updateEffect(i, { end: Math.max(e.start, Number(v) || 0) })}
              />
              <button className="text-red-500" onClick={() => removeEffect(i)}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* Export */}
      <Button
        type="primary"
        size="large"
        block
        loading={composing}
        onClick={compose}
        disabled={clips.length === 0}
      >
        {composing ? 'Đang xuất video…' : 'Xuất video'}
      </Button>
      {composing && (
        <div className="mt-3">
          <Progress percent={progress} status="active" />
          <div className="text-center ui-muted text-sm">{stage || 'Server đang render…'}</div>
        </div>
      )}

      {/* Result + post */}
      {result && (
        <div className="ui-card rounded-xl p-4 mt-5">
          <div className="font-semibold ui-text mb-3">Kết quả</div>
          <video src={result.url} controls className="w-full max-h-[70vh] rounded-lg bg-black mb-3" />

          {/* Thẻ mua sắm tương tác (Shoppertainment): hiện nút bấm được trên video theo mốc thời gian khi người xem xem */}
          <div className="rounded-lg ui-divider border p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium ui-text">🛒 Thẻ mua sắm (bấm được khi xem)</div>
              <Button size="small" icon={<Plus size={14} />} onClick={addHotspot}>Thêm thẻ</Button>
            </div>
            {hotspots.length === 0 ? (
              <p className="ui-muted text-xs">Gắn link sản phẩm/gói VIP hiện lên video ở khoảng thời gian bạn chọn — người xem bấm là mở link.</p>
            ) : (
              <div className="space-y-2">
                {hotspots.map((h, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <Input size="small" placeholder="Nhãn (VD: Mua ngay)" value={h.label} onChange={(e) => updHotspot(i, { label: e.target.value })} style={{ width: 150 }} />
                    <Input size="small" placeholder="https://link-san-pham" value={h.url} onChange={(e) => updHotspot(i, { url: e.target.value })} style={{ width: 200 }} />
                    <span className="text-xs ui-muted">giây</span>
                    <InputNumber size="small" min={0} value={h.start} onChange={(v) => updHotspot(i, { start: Number(v) || 0 })} style={{ width: 64 }} />
                    <span className="text-xs ui-muted">→</span>
                    <InputNumber size="small" min={0} value={h.end} onChange={(v) => updHotspot(i, { end: Number(v) || 0 })} style={{ width: 64 }} />
                    <button onClick={() => rmHotspot(i)} className="text-red-500" title="Xoá"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Input.TextArea
            rows={2}
            className="mb-3"
            placeholder="Viết caption cho video…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="primary" loading={posting} onClick={postVideo}>
              Đăng bài
            </Button>
            <Button onClick={() => navigate('/ads', { state: { studioVideoUrl: result.url } })}>
              Đưa vào quảng cáo
            </Button>
            <Button href={result.url} target="_blank">
              Tải về / Xem
            </Button>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudioPage;
